import { useRouteLoaderData } from "@remix-run/react";
import { useCallback, useEffect, useState } from "react";
import { useDarkModeAction } from "../actions/dark-mode.action";
import { darkModeAtom, type DarkModeState } from "../atoms/dark-mode.atom";
import { darkModeThemeColors } from "../utils/dark-mode";

function syncDocumentDarkMode(mode: DarkModeState) {
  document.documentElement.classList.toggle("dark", mode === "dark");
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", darkModeThemeColors[mode]);
}

export function useDarkMode() {
  const atomDarkMode = darkModeAtom.useValue();
  const rootLoaderData = useRouteLoaderData("root") as
    | { darkMode?: DarkModeState }
    | undefined;
  const darkModeAction = useDarkModeAction();
  const initialDarkMode = rootLoaderData?.darkMode ?? "light";
  const [isInitialized, setIsInitialized] = useState(false);
  const darkMode = isInitialized ? atomDarkMode : initialDarkMode;
  const isDark = darkMode === "dark";

  useEffect(() => {
    darkModeAtom.update(initialDarkMode);
    setIsInitialized(true);
  }, [initialDarkMode]);

  // Define setMode function first to avoid dependency issues
  const setMode = useCallback(
    async (mode: DarkModeState) => {
      const previousMode = darkMode;

      syncDocumentDarkMode(mode);
      darkModeAtom.update(mode);

      try {
        await darkModeAction({ darkMode: mode });
      } catch (error) {
        syncDocumentDarkMode(previousMode);
        darkModeAtom.update(previousMode);
        console.error("Failed to update dark mode preference:", error);
      }
    },
    [darkMode, darkModeAction],
  );

  const toggleDarkMode = async () => {
    const newMode: DarkModeState = isDark ? "light" : "dark";
    await setMode(newMode);
  };

  useEffect(() => {
    syncDocumentDarkMode(darkMode);
  }, [darkMode]);

  return {
    darkMode,
    isDark,
    toggleDarkMode,
    setMode,
  };
}
